import React, { useEffect, useRef, useId, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  X,
  Search,
  ShoppingBag,
  ArrowUpRight,
  Play,
  Plus,
  Minus,
} from "lucide-react";
import { safeUrl, money } from "./domain";
export function Button({ children, kind = "", className = "", ...props }) {
  return (
    <button className={`btn ${kind} ${className}`} {...props}>
      {children}
    </button>
  );
}
export function NumberInput({ value, onChange, onFocus, onBlur, ...props }) {
  const [draft, setDraft] = useState(String(value ?? ""));
  const focused = useRef(false);
  useEffect(() => { if (!focused.current) setDraft(String(value ?? "")); }, [value]);
  return <input {...props} type="number" value={draft}
    onFocus={e => { focused.current = true; if (e.target.value === "0") e.target.select(); onFocus?.(e); }}
    onChange={e => {
      const normalized = e.target.value.replace(/^(-?)0+(?=\d)/, "$1");
      e.target.value = normalized;
      setDraft(normalized); onChange?.(e);
    }}
    onBlur={e => {
      focused.current = false;
      const normalized = e.target.value === "" ? String(value ?? "") : String(Number(e.target.value));
      e.target.value = normalized; setDraft(normalized); onChange?.(e); onBlur?.(e);
    }} />;
}
export function Field({ label, children, hint, ...props }) {
  const id = useId();
  return (
    <label className="field" htmlFor={id}>
      <span>{label}</span>
      {children ? (
        React.cloneElement(children, { id: children.props.id || id })
      ) : (
        props.type === "number" ? <NumberInput id={id} {...props} /> : <input id={id} {...props} />
      )}
      {hint && <small>{hint}</small>}
    </label>
  );
}
export function Modal({ title, children, onClose, wide = false }) {
  const ref = useRef();
  useEffect(() => {
    const el = ref.current;
    el.showModal();
    const close = (e) => {
      e.preventDefault();
      onClose();
    };
    el.addEventListener("cancel", close);
    return () => {
      el.removeEventListener("cancel", close);
      el.close();
    };
  }, []);
  return (
    <dialog
      ref={ref}
      className={wide ? "wide" : ""}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      <header className="modal-head">
        <h2>{title}</h2>
        <button className="icon-btn" aria-label="Cerrar" onClick={onClose}>
          <X />
        </button>
      </header>
      <div className="modal-body">{children}</div>
    </dialog>
  );
}
export function Brand({ small = false, logo = "/assets/logo.png" }) {
  return (
    <a
      className={`brand ${small ? "small" : ""} ${!logo || logo === "/assets/logo.png" ? "original-logo" : ""}`}
      href="/"
      aria-label="Dilo Shop, inicio"
    >
      <img src={safeUrl(logo) || "/assets/logo.png"} alt="Dilo Shop" />
    </a>
  );
}
export function ProductImage({ product, variant, className = "" }) {
  const src = safeUrl(variant?.image) || safeUrl(product.images?.[0]);
  if (src)
    return (
      <div className={`product-image ${className}`}>
        <img src={src} alt={product.name} loading="lazy" />
      </div>
    );
  if (product.demo_image != null) {
    const i = product.demo_image;
    const x = [329, 516, 691, 854][i % 4],
      y = i < 4 ? 358 : 657;
    return (
      <div
        role="img"
        aria-label={product.name}
        className={`product-image demo-product ${className}`}
        style={{
          backgroundImage: "url(/assets/productos-referencia.png)",
          backgroundSize: `${(1855 / 135) * 100}% ${(918 / 135) * 100}%`,
          backgroundPosition: `${(x / (1855 - 135)) * 100}% ${(y / (918 - 135)) * 100}%`,
        }}
      />
    );
  }
  return (
    <div className={`product-image placeholder ${className}`}>
      <ShoppingBag size={48} />
      <span>Sin fotografía</span>
    </div>
  );
}
export function Video({ url }) {
  const src = safeUrl(url);
  if (!src) return null;
  const youtube = src.match(
    /(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
  );
  return youtube ? (
    <iframe
      title="Video del producto"
      src={`https://www.youtube-nocookie.com/embed/${youtube[1]}`}
      allowFullScreen
    />
  ) : (
    <video src={src} controls playsInline preload="metadata" />
  );
}
export function SearchBox({ value, onChange, placeholder = "Buscar…" }) {
  return (
    <label className="search">
      <Search size={19} />
      <input
        aria-label={placeholder}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {value && (
        <button aria-label="Limpiar búsqueda" onClick={() => onChange("")}>
          <X size={16} />
        </button>
      )}
    </label>
  );
}
export function Empty({
  title = "Todavía no hay registros",
  description,
  action,
}) {
  return (
    <div className="empty">
      <ShoppingBag size={34} />
      <h3>{title}</h3>
      {description && <p>{description}</p>}
      {action}
    </div>
  );
}
export function Status({ value }) {
  return (
    <span className={`status s-${value.replaceAll(" ", "-").toLowerCase()}`}>
      {value}
    </span>
  );
}
export function PageHead({ eyebrow, title, description, actions }) {
  return (
    <header className="page-head">
      <div>
        {eyebrow && <span className="eyebrow">{eyebrow}</span>}
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      <div className="actions">{actions}</div>
    </header>
  );
}
export function Quantity({ value, onChange, max = 999 }) {
  return (
    <div className="quantity">
      <button
        type="button"
        aria-label="Reducir cantidad"
        disabled={value <= 1}
        onClick={() => onChange(value - 1)}
      >
        <Minus size={15} />
      </button>
      <input
        aria-label="Cantidad"
        type="number"
        min="1"
        max={max}
        value={value}
        onChange={(e) =>
          onChange(Math.max(1, Math.min(max, Number(e.target.value))))
        }
      />
      <button
        type="button"
        aria-label="Aumentar cantidad"
        disabled={value >= max}
        onClick={() => onChange(value + 1)}
      >
        <Plus size={15} />
      </button>
    </div>
  );
}
export function ProductCard({ product, variants, onSelect, adminActions }) {
  const active = variants.filter(
      (v) => v.product_id === product.id && v.active,
    ),
    stock = active.reduce((s, v) => s + v.stock, 0);
  const discount =
    product.compare_price > product.price
      ? Math.round((1 - product.price / product.compare_price) * 100)
      : 0;
  return (
    <article className="product-card">
      <button
        className="product-photo-button"
        onClick={() => onSelect(product)}
        aria-label={`Ver ${product.name}`}
      >
        <ProductImage product={product} />
        {discount > 0 && <span className="sale-tag">−{discount}%</span>}
        {product.video_url && (
          <span className="video-tag">
            <Play size={12} /> Video
          </span>
        )}
      </button>
      <div className="product-info">
        <div className="swatches tiny">
          {active.map((v) => (
            <span key={v.id} title={v.color} style={{ background: v.hex }} />
          ))}
        </div>
        <h3>
          <button onClick={() => onSelect(product)}>{product.name}</button>
        </h3>
        <div className="product-price">
          <strong>{money(product.price)}</strong>
          {discount > 0 && <del>{money(product.compare_price)}</del>}
        </div>
        {adminActions || (
          <Button
            className="buy-btn"
            disabled={!stock}
            onClick={() => onSelect(product)}
          >
            {stock ? "COMPRAR" : "AGOTADO"}
            {stock > 0 && <ArrowUpRight size={18} />}
          </Button>
        )}
      </div>
    </article>
  );
}
export function download(content, name, type = "text/plain;charset=utf-8") {
  const blob =
    content instanceof Blob ? content : new Blob([content], { type });
  const href = URL.createObjectURL(blob),
    a = document.createElement("a");
  a.href = href;
  a.download = name;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 2000);
}

// Accessible, non-blocking confirmation used before stock/payment changes.
export function askConfirm(message) {
  return new Promise((resolve) => {
    const host = document.createElement("div");
    document.body.append(host);
    const root = createRoot(host);
    const finish = (value) => {
      resolve(value);
      setTimeout(() => {
        root.unmount();
        host.remove();
      }, 0);
    };
    root.render(
      <Modal title="Confirmar cambio" onClose={() => finish(false)}>
        <p>{message}</p>
        <div className="actions" style={{ justifyContent: "flex-end" }}>
          <Button kind="outline" onClick={() => finish(false)}>
            Volver
          </Button>
          <Button onClick={() => finish(true)}>Sí, confirmar</Button>
        </div>
      </Modal>,
    );
  });
}
